/** Inline stroke icons; no icon font, no runtime fetch. */
type IconProps = { size?: number; className?: string };

function svgProps(size: number) {
  return {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
}

export function IconBack({ size = 22 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

export function IconCompose({ size = 20 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function IconSend({ size = 19 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M4.5 12h13" />
      <path d="M11.5 6l6 6-6 6" />
    </svg>
  );
}

export function IconStop({ size = 18 }: IconProps) {
  return (
    <svg {...svgProps(size)} strokeWidth={0} fill="currentColor">
      <rect x="7" y="7" width="10" height="10" rx="2" />
    </svg>
  );
}

export function IconSearch({ size = 17 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.6-3.6" />
    </svg>
  );
}

export function IconMore({ size = 20 }: IconProps) {
  return (
    <svg {...svgProps(size)} strokeWidth={0} fill="currentColor">
      <circle cx="12" cy="5" r="1.7" />
      <circle cx="12" cy="12" r="1.7" />
      <circle cx="12" cy="19" r="1.7" />
    </svg>
  );
}

export function IconInstall({ size = 19 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M12 3v11M8 10.5l4 4 4-4" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  );
}

export function IconBell({ size = 19 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M18 8a6 6 0 0 0-12 0c0 5-2 6.5-2 6.5h16S18 13 18 8z" />
      <path d="M10.3 20a2 2 0 0 0 3.4 0" />
    </svg>
  );
}

export function IconModel({ size = 19 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M12 3l7.5 4.3v9.4L12 21l-7.5-4.3V7.3z" />
      <path d="M12 12l7.5-4.3M12 12v9M12 12L4.5 7.7" />
    </svg>
  );
}

export function IconSpeaker({ size = 19 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M11 5L6.5 9H3v6h3.5L11 19z" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" />
    </svg>
  );
}

export function IconPaperclip({ size = 20 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M20 11.5l-7.6 7.6a4.5 4.5 0 0 1-6.4-6.4l7.7-7.6a3 3 0 0 1 4.3 4.3l-7.7 7.6a1.5 1.5 0 0 1-2.1-2.1l7-7" />
    </svg>
  );
}

export function IconClose({ size = 16 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

export function IconImage({ size = 18 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <circle cx="8.5" cy="10" r="1.5" />
      <path d="M21 16l-4.5-4.5L7 21" />
    </svg>
  );
}

export function IconFile({ size = 18 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M14 3v5h5" />
      <path d="M19 8v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7z" />
    </svg>
  );
}

export function IconMoreHorizontal({ size = 20 }: IconProps) {
  return (
    <svg {...svgProps(size)} strokeWidth={0} fill="currentColor">
      <circle cx="5" cy="12" r="1.7" />
      <circle cx="12" cy="12" r="1.7" />
      <circle cx="19" cy="12" r="1.7" />
    </svg>
  );
}

/** Marks the collapsed intermediate steps of a turn. */
export function IconSteps({ size = 16 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M4 7h5M4 12h9M4 17h6" />
      <circle cx="17.5" cy="7" r="2" />
      <circle cx="19.5" cy="17" r="2" />
    </svg>
  );
}

export function IconSettings({ size = 20 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M19.4 15a1.7 1.7 0 00.34 1.88l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.7 1.7 0 00-1.88-.34 1.7 1.7 0 00-1.03 1.56V21a2 2 0 11-4 0v-.1A1.7 1.7 0 008.4 19.3a1.7 1.7 0 00-1.88.34l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.7 1.7 0 00.34-1.88 1.7 1.7 0 00-1.56-1.03H2a2 2 0 110-4h.1A1.7 1.7 0 003.7 8.4a1.7 1.7 0 00-.34-1.88l-.06-.06a2 2 0 112.83-2.83l.06.06a1.7 1.7 0 001.88.34H8.4A1.7 1.7 0 009.43 2.5V2a2 2 0 114 0v.1a1.7 1.7 0 001.03 1.56 1.7 1.7 0 001.88-.34l.06-.06a2 2 0 112.83 2.83l-.06.06a1.7 1.7 0 00-.34 1.88v.07a1.7 1.7 0 001.56 1.03H22a2 2 0 110 4h-.1a1.7 1.7 0 00-1.56 1.03z" />
    </svg>
  );
}

export function IconPin({ size = 13 }: IconProps) {
  return (
    <svg {...svgProps(size)} strokeWidth={0} fill="currentColor">
      <path d="M14.5 2.5l7 7-2 2-1.2-1.2-3.6 3.6.5 4.3-2 2-4-4-4.3 4.3-1.4-1.4L8.8 14.8l-4-4 2-2 4.3.5 3.6-3.6L13.5 4.5z" />
    </svg>
  );
}

export function IconArchive({ size = 19 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v11a1 1 0 001 1h12a1 1 0 001-1V8" />
      <path d="M10 12h4" />
    </svg>
  );
}

export function IconTrash({ size = 19 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M4 7h16" />
      <path d="M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2" />
      <path d="M6 7v13a1 1 0 001 1h10a1 1 0 001-1V7" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

export function IconRename({ size = 19 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" />
    </svg>
  );
}

export function IconRestore({ size = 19 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M3 12a9 9 0 109-9 9 9 0 00-6.4 2.6L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  );
}

export function IconChevronDown({ size = 18 }: IconProps) {
  return (
    <svg {...svgProps(size)}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}
